import { supabase } from '../supabase/supabaseClient';

/**
 * Group Outlook Data Structure (Historical Intelligence)
 */
export interface GroupOutlook {
  ticker: string;

  // Overall Intelligence
  overall_sentiment: string; // 'bullish', 'bearish', 'neutral', etc.
  confidence_score: number;
  consensus_reached: boolean;

  // Historical Tracking
  first_mentioned_at: string | null;
  last_discussed_at: string | null;
  most_active_day: string | null;
  total_messages: number;
  total_participants: number;
  active_days_count: number;

  // Timeline Data (for charts)
  sentiment_history: Array<{
    date: string;
    bullish: number;
    bearish: number;
    neutral: number;
  }>;

  // Turning Points
  turning_points: Array<{
    date: string;
    from_bullish: number;
    to_bullish: number;
    shift_magnitude: number;
    direction: string;
  }>;

  // AI-Generated Insights
  executive_summary: string | null;

  // Key Moments Timeline
  key_moments: Array<{
    date: string;
    event: string;
    sentiment: string;
    importance: number;
  }>;

  // Participants
  participants: Array<{
    user_id: string;
    user_name: string;
    message_count: number;
  }>;

  // Influencers
  influencers: Array<{
    user_id: string;
    user_name: string;
    total_messages: number;
    influence_score: number;
  }>;

  // Related Tickers
  related_tickers: string[];

  // Recent Daily Facts (last 7 days)
  recent_daily_facts: Array<{
    date: string;
    message_count: number;
    sentiment_bullish: number;
    sentiment_bearish: number;
    daily_summary: string | null;
    key_phrases: string[];
  }>;

  // Metadata
  computed_at: string;
  based_on_facts_until: string | null;

  // Legacy compatibility (for existing UI)
  window: {
    duration_minutes: number;
    message_count: number;
    mention_count: number;
  };
  context: {
    confidence: number;
    source: string;
    related_tickers: Array<{
      ticker: string;
      count: number;
      relevance: number;
    }>;
  };
  sentiment: {
    bullish: number;
    bearish: number;
    neutral: number;
    confidence: number;
  };
  key_phrases?: string[];
  summary?: string;
}

/**
 * Group Outlook Service (Historical Intelligence)
 * Now uses persistent historical data instead of 10-minute window
 */
class GroupOutlookService {
  /**
   * Get comprehensive historical outlook for a ticker in a group
   * This fetches from the historical intelligence system
   */
  async getTickerOutlook(
    groupId: string,
    ticker: string
  ): Promise<{ data: GroupOutlook | null; error: string | null }> {
    try {
      console.log(
        `📊 [GroupOutlook] Fetching historical intelligence for $${ticker} in group ${groupId}`
      );

      const tickerUpper = ticker.toUpperCase();

      // Fetch the evolving intelligence
      const { data: intelligence, error: intelligenceError } = await supabase
        .from('stock_group_intelligence')
        .select('*')
        .eq('group_id', groupId)
        .eq('ticker', tickerUpper)
        .single();

      if (intelligenceError && intelligenceError.code !== 'PGRST116') {
        console.error(`❌ [GroupOutlook] Error fetching intelligence:`, intelligenceError);
      }

      // Fetch recent daily facts (last 30 days)
      const { data: dailyFacts, error: factsError } = await supabase
        .from('stock_daily_facts')
        .select('*')
        .eq('group_id', groupId)
        .eq('ticker', tickerUpper)
        .order('date', { ascending: false })
        .limit(30);

      if (factsError) {
        console.error(`❌ [GroupOutlook] Error fetching daily facts:`, factsError);
      }

      // If no historical data exists, fallback to real-time analysis of existing messages
      if (!intelligence && (!dailyFacts || dailyFacts.length === 0)) {
        console.log(
          `📊 [GroupOutlook] No historical data found, analyzing existing messages for $${ticker}...`
        );

        // Smart fallback: analyze existing messages directly
        const realtimeOutlook = await this.analyzeMessagesRealtime(groupId, ticker);

        if (realtimeOutlook) {
          console.log(
            `✅ [GroupOutlook] Generated real-time outlook for $${ticker} from ${realtimeOutlook.total_messages} messages`
          );
          return { data: realtimeOutlook, error: null };
        }

        // Only return empty if truly no messages exist
        console.log(`📊 [GroupOutlook] No messages found for $${ticker}`);
        return {
          data: this.getEmptyOutlook(ticker),
          error: null,
        };
      }

      // Build comprehensive outlook from historical data
      const outlook = this.buildOutlookFromHistoricalData(ticker, intelligence, dailyFacts || []);

      console.log(`✅ [GroupOutlook] Historical intelligence loaded for $${ticker}`);
      return { data: outlook, error: null };
    } catch (error) {
      console.error(`❌ [GroupOutlook] Error fetching ticker outlook:`, error);
      return { data: null, error: 'Failed to fetch ticker outlook' };
    }
  }

  /**
   * Build comprehensive outlook from historical intelligence data
   */
  private buildOutlookFromHistoricalData(
    ticker: string,
    intelligence: any | null,
    dailyFacts: any[]
  ): GroupOutlook {
    // Get most recent sentiment (for legacy compatibility)
    const mostRecent = dailyFacts[0];
    const currentSentiment = mostRecent
      ? {
          bullish: mostRecent.sentiment_bullish || 0,
          bearish: mostRecent.sentiment_bearish || 0,
          neutral: mostRecent.sentiment_neutral || 0,
        }
      : { bullish: 0, bearish: 0, neutral: 0 };

    // Extract recent daily facts (last 7 days) with full details
    const recentFacts = dailyFacts.slice(0, 7).map(fact => ({
      date: fact.date,
      message_count: fact.message_count,
      sentiment_bullish: fact.sentiment_bullish,
      sentiment_bearish: fact.sentiment_bearish,
      daily_summary: fact.daily_summary,
      key_phrases: fact.key_phrases || [],
    }));

    // Calculate total duration in days (for legacy window)
    const daysCount = dailyFacts.length;
    const totalMessages =
      intelligence?.total_messages || dailyFacts.reduce((sum, day) => sum + day.message_count, 0);

    // Extract participants from daily facts
    const participantsMap = new Map<string, { user_name: string; count: number }>();
    dailyFacts.forEach(day => {
      if (day.participants) {
        day.participants.forEach((p: any) => {
          const existing = participantsMap.get(p.user_id) || { user_name: p.user_name, count: 0 };
          existing.count += p.message_count;
          participantsMap.set(p.user_id, existing);
        });
      }
    });

    const participants = Array.from(participantsMap.entries())
      .map(([user_id, data]) => ({
        user_id,
        user_name: data.user_name,
        message_count: data.count,
      }))
      .sort((a, b) => b.message_count - a.message_count);

    // Extract related tickers from recent facts
    const relatedTickersSet = new Set<string>();
    dailyFacts.slice(0, 7).forEach(day => {
      if (day.related_tickers) {
        day.related_tickers.forEach((t: string) => relatedTickersSet.add(t));
      }
    });

    return {
      ticker,

      // Historical Intelligence (Evolving)
      overall_sentiment: intelligence?.overall_sentiment || 'neutral',
      confidence_score: intelligence?.confidence_score || 0,
      consensus_reached: intelligence?.consensus_reached || false,

      // Historical Tracking
      first_mentioned_at: intelligence?.first_mentioned_at || null,
      last_discussed_at: intelligence?.last_discussed_at || null,
      most_active_day: intelligence?.most_active_day || null,
      total_messages: totalMessages,
      total_participants: intelligence?.total_participants || participants.length,
      active_days_count: daysCount,

      // Timeline Data (Deterministic - from daily facts)
      sentiment_history: (intelligence?.sentiment_history || []) as any[],

      // Turning Points (Detected retrospectively)
      turning_points: (intelligence?.turning_points || []) as any[],

      // AI-Generated Executive Summary (Evolving)
      executive_summary: intelligence?.executive_summary || null,

      // Key Moments Timeline
      key_moments: (intelligence?.key_moments || []) as any[],

      // Participants
      participants,

      // Influencers (from intelligence)
      influencers: (intelligence?.influencers || []) as any[],

      // Related Tickers
      related_tickers: Array.from(relatedTickersSet),

      // Recent Daily Facts
      recent_daily_facts: recentFacts,

      // Metadata
      computed_at: intelligence?.computed_at || new Date().toISOString(),
      based_on_facts_until: intelligence?.based_on_facts_until || null,

      // Legacy compatibility
      window: {
        duration_minutes: daysCount * 24 * 60, // Convert days to minutes
        message_count: totalMessages,
        mention_count: dailyFacts.reduce((sum, day) => sum + day.mention_count, 0),
      },
      context: {
        confidence: intelligence?.confidence_score || 0,
        source: 'historical',
        related_tickers: Array.from(relatedTickersSet).map(t => ({
          ticker: t,
          count: 1,
          relevance: 0.5,
        })),
      },
      sentiment: {
        ...currentSentiment,
        confidence: intelligence?.confidence_score || 0,
      },
      key_phrases: mostRecent?.key_phrases || [],
      summary:
        intelligence?.executive_summary ||
        this.generateFallbackSummary(ticker, daysCount, totalMessages, currentSentiment),
    };
  }

  /**
   * Get empty outlook when no data exists
   */
  private getEmptyOutlook(ticker: string): GroupOutlook {
    return {
      ticker,
      overall_sentiment: 'neutral',
      confidence_score: 0,
      consensus_reached: false,
      first_mentioned_at: null,
      last_discussed_at: null,
      most_active_day: null,
      total_messages: 0,
      total_participants: 0,
      active_days_count: 0,
      sentiment_history: [],
      turning_points: [],
      executive_summary: `No historical discussion found for $${ticker} yet.`,
      key_moments: [],
      participants: [],
      influencers: [],
      related_tickers: [],
      recent_daily_facts: [],
      computed_at: new Date().toISOString(),
      based_on_facts_until: null,
      window: {
        duration_minutes: 0,
        message_count: 0,
        mention_count: 0,
      },
      context: {
        confidence: 0,
        source: 'historical',
        related_tickers: [],
      },
      sentiment: {
        bullish: 0,
        bearish: 0,
        neutral: 0,
        confidence: 0,
      },
      key_phrases: [],
      summary: `No discussion about $${ticker} found yet. Start the conversation!`,
    };
  }

  /**
   * Generate fallback summary when AI summary doesn't exist
   */
  private generateFallbackSummary(
    ticker: string,
    daysCount: number,
    totalMessages: number,
    sentiment: { bullish: number; bearish: number; neutral: number }
  ): string {
    const sentimentLabel =
      sentiment.bullish > 0.6 ? 'bullish' : sentiment.bearish > 0.6 ? 'bearish' : 'mixed';

    return `Group has discussed $${ticker} over ${daysCount} ${daysCount === 1 ? 'day' : 'days'} with ${sentimentLabel} sentiment (${totalMessages} total messages).`;
  }

  /**
   * Smart Fallback: Analyze existing messages in real-time
   * This is used when historical tables haven't been populated yet
   */
  private async analyzeMessagesRealtime(
    groupId: string,
    ticker: string
  ): Promise<GroupOutlook | null> {
    try {
      const tickerUpper = ticker.toUpperCase();

      console.log(`🔍 [GroupOutlook] Fetching all messages containing $${tickerUpper}...`);

      // Fetch ALL messages that mention this ticker
      const { data: messages, error } = await supabase
        .from('messages')
        .select('id, content, created_at, user_id, profiles(name)')
        .eq('group_id', groupId)
        .or(`content.ilike.%${tickerUpper}%,content.ilike.%$${tickerUpper}%`)
        .order('created_at', { ascending: true });

      if (error) {
        console.error(`❌ [GroupOutlook] Error fetching messages:`, error);
        return null;
      }

      if (!messages || messages.length === 0) {
        console.log(`📊 [GroupOutlook] No messages found for $${tickerUpper}`);
        return null;
      }

      console.log(`✅ [GroupOutlook] Found ${messages.length} messages mentioning $${tickerUpper}`);

      // Analyze the messages
      return this.buildOutlookFromMessages(ticker, messages);
    } catch (error) {
      console.error(`❌ [GroupOutlook] Error in real-time analysis:`, error);
      return null;
    }
  }

  /**
   * Build outlook by analyzing raw messages (fallback when historical data doesn't exist)
   */
  private buildOutlookFromMessages(ticker: string, messages: any[]): GroupOutlook {
    console.log(`🔬 [GroupOutlook] Analyzing ${messages.length} messages for $${ticker}...`);

    // Extract dates and group by day
    const messagesByDay = new Map<string, any[]>();
    messages.forEach(msg => {
      const date = msg.created_at.split('T')[0]; // Get date only (YYYY-MM-DD)
      if (!messagesByDay.has(date)) {
        messagesByDay.set(date, []);
      }
      messagesByDay.get(date)!.push(msg);
    });

    const activeDays = Array.from(messagesByDay.keys()).sort();

    // Calculate sentiment using keyword analysis
    const bullishKeywords = [
      'buy',
      'bullish',
      'moon',
      'up',
      'gains',
      'surge',
      'rally',
      'boom',
      'profit',
      'strong',
      'growth',
      'breakout',
      'long',
      'calls',
      'uptrend',
      'rocket',
      '🚀',
      '📈',
      '💰',
      'amazing',
      'great',
      'excellent',
      'beat',
      'love',
    ];

    const bearishKeywords = [
      'sell',
      'bearish',
      'down',
      'loss',
      'drop',
      'crash',
      'dump',
      'falling',
      'weak',
      'decline',
      'breakdown',
      'puts',
      'short',
      'downtrend',
      '📉',
      '💩',
      'concern',
      'worried',
      'bad',
      'terrible',
      'miss',
      'disappoint',
    ];

    let bullishCount = 0;
    let bearishCount = 0;
    let neutralCount = 0;

    messages.forEach(msg => {
      const content = msg.content.toLowerCase();
      const hasBullish = bullishKeywords.some(keyword => content.includes(keyword));
      const hasBearish = bearishKeywords.some(keyword => content.includes(keyword));

      if (hasBullish && !hasBearish) {
        bullishCount++;
      } else if (hasBearish && !hasBullish) {
        bearishCount++;
      } else {
        neutralCount++;
      }
    });

    const totalSentimentMessages = bullishCount + bearishCount + neutralCount;
    const sentiment = {
      bullish: totalSentimentMessages > 0 ? bullishCount / totalSentimentMessages : 0,
      bearish: totalSentimentMessages > 0 ? bearishCount / totalSentimentMessages : 0,
      neutral: totalSentimentMessages > 0 ? neutralCount / totalSentimentMessages : 0,
      confidence: Math.min(messages.length / 10, 1), // Higher confidence with more messages
    };

    // Determine overall sentiment
    let overallSentiment = 'neutral';
    if (sentiment.bullish > 0.5) overallSentiment = 'bullish';
    else if (sentiment.bearish > 0.5) overallSentiment = 'bearish';
    else if (sentiment.bullish > sentiment.bearish && sentiment.bullish > 0.3)
      overallSentiment = 'bullish';
    else if (sentiment.bearish > sentiment.bullish && sentiment.bearish > 0.3)
      overallSentiment = 'bearish';

    // Extract participants
    const participantsMap = new Map<string, { user_name: string; count: number }>();
    messages.forEach(msg => {
      const userId = msg.user_id;
      const userName = msg.profiles?.name || 'Unknown';
      const existing = participantsMap.get(userId) || { user_name: userName, count: 0 };
      existing.count++;
      participantsMap.set(userId, existing);
    });

    const participants = Array.from(participantsMap.entries())
      .map(([user_id, data]) => ({
        user_id,
        user_name: data.user_name,
        message_count: data.count,
      }))
      .sort((a, b) => b.message_count - a.message_count);

    // Extract key phrases (most frequent words, excluding common words)
    const commonWords = new Set([
      'the',
      'a',
      'an',
      'is',
      'it',
      'to',
      'and',
      'of',
      'in',
      'for',
      'on',
      'with',
    ]);
    const wordFrequency = new Map<string, number>();

    messages.forEach(msg => {
      const words = msg.content
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, ' ')
        .split(/\s+/)
        .filter(w => w.length > 3 && !commonWords.has(w));

      words.forEach(word => {
        wordFrequency.set(word, (wordFrequency.get(word) || 0) + 1);
      });
    });

    const keyPhrases = Array.from(wordFrequency.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([word]) => word);

    // Generate timeline data
    const sentimentHistory = activeDays.map(date => {
      const dayMessages = messagesByDay.get(date)!;
      let dayBullish = 0,
        dayBearish = 0,
        dayNeutral = 0;

      dayMessages.forEach(msg => {
        const content = msg.content.toLowerCase();
        const hasBullish = bullishKeywords.some(keyword => content.includes(keyword));
        const hasBearish = bearishKeywords.some(keyword => content.includes(keyword));

        if (hasBullish && !hasBearish) dayBullish++;
        else if (hasBearish && !hasBullish) dayBearish++;
        else dayNeutral++;
      });

      const total = dayBullish + dayBearish + dayNeutral;
      return {
        date,
        bullish: total > 0 ? dayBullish / total : 0,
        bearish: total > 0 ? dayBearish / total : 0,
        neutral: total > 0 ? dayNeutral / total : 0,
      };
    });

    // Detect turning points (>20% sentiment shift)
    const turningPoints = [];
    for (let i = 1; i < sentimentHistory.length; i++) {
      const prev = sentimentHistory[i - 1];
      const curr = sentimentHistory[i];
      const shift = Math.abs(curr.bullish - prev.bullish);

      if (shift > 0.2) {
        turningPoints.push({
          date: curr.date,
          from_bullish: prev.bullish,
          to_bullish: curr.bullish,
          shift_magnitude: shift,
          direction: curr.bullish > prev.bullish ? 'bullish' : 'bearish',
        });
      }
    }

    // Find most active day
    let mostActiveDay = activeDays[0];
    let maxMessages = 0;
    messagesByDay.forEach((msgs, date) => {
      if (msgs.length > maxMessages) {
        maxMessages = msgs.length;
        mostActiveDay = date;
      }
    });

    // Generate summary
    const daysSpan = activeDays.length;
    const sentimentLabel = overallSentiment.toUpperCase();
    const summary = `Group has discussed $${ticker} over ${daysSpan} ${daysSpan === 1 ? 'day' : 'days'} with ${sentimentLabel} sentiment. ${messages.length} total messages from ${participants.length} ${participants.length === 1 ? 'participant' : 'participants'}.`;

    console.log(
      `✅ [GroupOutlook] Real-time analysis complete: ${overallSentiment} sentiment, ${messages.length} messages`
    );

    return {
      ticker,
      overall_sentiment: overallSentiment,
      confidence_score: sentiment.confidence,
      consensus_reached: sentiment.confidence > 0.7,
      first_mentioned_at: messages[0].created_at,
      last_discussed_at: messages[messages.length - 1].created_at,
      most_active_day: mostActiveDay,
      total_messages: messages.length,
      total_participants: participants.length,
      active_days_count: activeDays.length,
      sentiment_history: sentimentHistory,
      turning_points: turningPoints,
      executive_summary: summary,
      key_moments: turningPoints.slice(0, 5).map(tp => ({
        date: tp.date,
        event: `Sentiment shifted ${tp.direction}`,
        sentiment: tp.direction,
        importance: tp.shift_magnitude,
      })),
      participants,
      influencers: participants.slice(0, 5).map((p, idx) => ({
        user_id: p.user_id,
        user_name: p.user_name,
        total_messages: p.message_count,
        influence_score: 1 - idx * 0.15, // Simple scoring: 1.0, 0.85, 0.70, 0.55, 0.40
      })),
      related_tickers: [], // TODO: Extract other tickers mentioned
      recent_daily_facts: activeDays.slice(-7).map(date => ({
        date,
        message_count: messagesByDay.get(date)!.length,
        sentiment_bullish: sentimentHistory.find(s => s.date === date)?.bullish || 0,
        sentiment_bearish: sentimentHistory.find(s => s.date === date)?.bearish || 0,
        daily_summary: null,
        key_phrases: [],
      })),
      computed_at: new Date().toISOString(),
      based_on_facts_until: activeDays[activeDays.length - 1] || null,
      window: {
        duration_minutes: activeDays.length * 24 * 60,
        message_count: messages.length,
        mention_count: messages.length,
      },
      context: {
        confidence: sentiment.confidence,
        source: 'realtime',
        related_tickers: [],
      },
      sentiment,
      key_phrases: keyPhrases,
      summary,
    };
  }
}

// Export singleton instance
export const groupOutlookService = new GroupOutlookService();
